import { Paragraph } from '@/components/common/Typography';
import IdvIntro from '@/components/idv/IdvIntro';
import { useTrackPageViewed } from '@/services/analytics/useTracking';
import { PO_BOX_REGEXP } from '@common/regexp';
import { typedKeys } from '@common/util/objects';
import { getIsValidZipCode } from '@common/util/strings';
import { useState } from 'react';
import { IdvForm, IdvFormType } from '../IdvForm';

type T = IdvFormType<
    undefined,
    'address1' | 'locality' | 'region' | 'postalCode' | 'address2'
>;

export const AddressView = () => {
    useTrackPageViewed('IDV > AddressView');

    const [values, setValues] = useState<T>();
    const [errors, setErrors] = useState<T>({});

    const updateValues = (change: Partial<T>) => {
        setValues({ ...values, ...change });
    };

    return (
        <>
            <IdvIntro
                title="Where do you live?"
                desc={
                    <>
                        <Paragraph>
                            We use this information to verify your identity; if
                            you’ve moved in the past 6 months, please provide
                            the address that your credit cards or most recent
                            tax return is associated with.
                        </Paragraph>
                    </>
                }
            />
            <IdvForm<T>
                initValues={(userProfile) => ({
                    address1: userProfile?.address1 || undefined,
                    locality: userProfile?.locality || undefined,
                    region: userProfile?.region || undefined,
                    postalCode: userProfile?.postalCode || undefined,
                    address2: userProfile?.address2 || undefined,
                })}
                toMutationInput={(values) => {
                    return values;
                }}
                backNextNavProps={{
                    onClickEventParams: {
                        location: 'IDV > AddressView',
                    },
                }}
            >
                <IdvForm.Row>
                    <IdvForm.Input<T>
                        name="address1"
                        label="Street"
                        placeholder="Start typing to autocomplete your address"
                        type="address"
                        error={errors.address1 ?? undefined}
                        onChange={(address, updateValues) => {
                            if (PO_BOX_REGEXP.test(address.address1)) {
                                setErrors({
                                    ...errors,
                                    address1:
                                        'PO boxes cannot be used for initial account onboarding; please provide a non PO box address.',
                                });
                                address.address1 = '';
                            } else {
                                setErrors({ ...errors, address1: null });
                            }

                            updateValues?.(
                                typedKeys(address).reduce((acc, key) => {
                                    const value =
                                        values?.[key as keyof typeof values];
                                    if (!value || key === 'address1')
                                        return { ...acc, [key]: address[key] };
                                    return acc;
                                }, values || {})
                            );
                        }}
                        autoFocus
                        isRequired
                    />
                </IdvForm.Row>
                <IdvForm.Row>
                    <IdvForm.Input<T>
                        name="locality"
                        label="City"
                        placeholder="eg. San Francisco"
                        onChange={(locality) => updateValues({ locality })}
                        isRequired
                    />
                    <IdvForm.Input<T>
                        name="region"
                        label="State"
                        type="usaState"
                        placeholder="eg. California"
                        onChange={(region) => updateValues({ region })}
                        isRequired
                    />
                </IdvForm.Row>
                <IdvForm.Row>
                    <IdvForm.Input<T>
                        name="postalCode"
                        label="Zip code"
                        placeholder="eg. 94941"
                        onChange={(postalCode) => updateValues({ postalCode })}
                        isRequired
                        validateOnBlur
                        validations={[
                            {
                                validate: getIsValidZipCode,
                                error: 'Please enter a valid zip code',
                            },
                        ]}
                    />
                    <IdvForm.Input<T>
                        name="address2"
                        label="Apt number"
                        placeholder="If applicable"
                    />
                </IdvForm.Row>
            </IdvForm>
        </>
    );
};
